/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import {
  FaArrowUp,
  FaArrowDown,
  FaSearch,
  FaFileExcel,
  FaStore,
  FaUserPlus,
  FaUserMinus,
  FaBalanceScale,
  FaPercentage,
  FaBuilding,
  FaMapMarkerAlt,
  FaExclamationTriangle,
} from 'react-icons/fa'
import {
  KpiGrid,
  KpiCard,
  TableWrapper,
  TableControls,
  SearchInputWrapper,
  ExportButton,
  TableScroll,
  StyledTable,
  TrendBadge,
  InvasionBadge,
} from './styles'
import {
  CoberturaAnaliticaResponse,
  CoberturaPorUF,
  CoberturaPorDistribuidor,
  ExportCoverageExcelOptions,
} from './types'

interface CoverageEvolutionTabProps {
  data: CoberturaAnaliticaResponse
  onExportExcel: (options?: ExportCoverageExcelOptions) => void
}

export const CoverageEvolutionTab: React.FC<CoverageEvolutionTabProps> = ({
  data,
  onExportExcel,
}) => {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const [viewMode, setViewMode] = useState<'uf' | 'distribuidor'>('uf')
  const [searchTerm, setSearchTerm] = useState('')
  const { nacional, porUf, porDistribuidor, periodo } = data

  const ufsFiltradas = useMemo(() => {
    if (!searchTerm.trim()) return porUf.filter((u) => u.cobertura_atual > 0 || u.cobertura_anterior > 0)
    const term = searchTerm.toLowerCase().trim()
    return porUf.filter(
      (item) =>
        item.uf.toLowerCase().includes(term) || item.estado.toLowerCase().includes(term),
    )
  }, [porUf, searchTerm])

  const distsFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return porDistribuidor
    const term = searchTerm.toLowerCase().trim()
    return porDistribuidor.filter(
      (item) =>
        item.distribuidor.toLowerCase().includes(term) ||
        item.cod_dist.toLowerCase().includes(term) ||
        item.regional.toLowerCase().includes(term) ||
        (item.uf_principal && item.uf_principal.toLowerCase().includes(term)) ||
        item.uf.toLowerCase().includes(term) ||
        (item.ufs_invasao &&
          item.ufs_invasao.some((u) => u.uf.toLowerCase().includes(term))),
    )
  }, [porDistribuidor, searchTerm])

  return (
    <>
      {/* Cards de KPIs com Foco em Dinâmica de Carteira */}
      <KpiGrid>
        <KpiCard $borderColor='#38bdf8'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Cobertura Atual</span>
            <FaStore style={{ color: '#38bdf8', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#38bdf8', fontSize: '26px', fontWeight: 700 }}>
            {(nacional.cobertura_atual || 0).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Período: {periodo.atual.startDate} até {periodo.atual.endDate}</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor='#64748b'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Cobertura Anterior</span>
            <FaStore style={{ color: '#94a3b8', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#ffffff', fontSize: '26px', fontWeight: 700 }}>
            {(nacional.cobertura_anterior || 0).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Período: {periodo.anterior.startDate} até {periodo.anterior.endDate}</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor='#22c55e'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>PDVs Ganhos (Novos)</span>
            <FaUserPlus style={{ color: '#22c55e', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#4ade80', fontSize: '26px', fontWeight: 700 }}>
            +{(nacional.ganhos || 0).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Clientes positivados sem compra anterior</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor='#ef4444'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>PDVs Perdidos (Churn)</span>
            <FaUserMinus style={{ color: '#ef4444', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#f87171', fontSize: '26px', fontWeight: 700 }}>
            -{(nacional.perdidos || 0).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Compraram no anterior e não no atual</span>
          </div>
        </KpiCard>

        <KpiCard
          $borderColor={
            nacional.saldo_liquido >= 0 ? '#a6c10b' : '#ef4444'
          }
        >
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Saldo Líquido</span>
            <FaBalanceScale style={{ color: nacional.saldo_liquido >= 0 ? '#a6c10b' : '#ef4444', fontSize: '18px' }} />
          </div>
          <div
            className='kpi-value'
            style={{
              color: nacional.saldo_liquido >= 0 ? '#a6c10b' : '#f87171',
              fontSize: '26px',
              fontWeight: 700,
            }}
          >
            {nacional.saldo_liquido >= 0 ? '+' : ''}
            {(nacional.saldo_liquido || 0).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <TrendBadge
              $type={
                nacional.taxa_evolucao_pct > 0
                  ? 'positive'
                  : nacional.taxa_evolucao_pct < 0
                  ? 'negative'
                  : 'neutral'
              }
            >
              {nacional.taxa_evolucao_pct >= 0 ? <FaArrowUp /> : <FaArrowDown />}
              {nacional.taxa_evolucao_pct >= 0 ? '+' : ''}
              {nacional.taxa_evolucao_pct.toFixed(2)}% vs anterior
            </TrendBadge>
          </div>
        </KpiCard>

        <KpiCard $borderColor='#f59e0b'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Taxa de Churn</span>
            <FaPercentage style={{ color: '#f59e0b', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#fbbf24', fontSize: '26px', fontWeight: 700 }}>
            {(nacional.taxa_churn_pct || 0).toFixed(2)}%
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Perda relativa sobre a base anterior</span>
          </div>
        </KpiCard>
      </KpiGrid>

      {/* Tabela Comparativa de Dinâmica */}
      <div style={{ padding: '0 10px' }}>
        <TableWrapper>
          <TableControls>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div
                style={{
                  display: 'flex',
                  background: 'rgba(0, 24, 66, 0.6)',
                  borderRadius: '8px',
                  padding: '3px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <button
                  type='button'
                  onClick={() => setViewMode('uf')}
                  style={{
                    background: viewMode === 'uf' ? '#a6c10b' : 'transparent',
                    color: viewMode === 'uf' ? '#091c3e' : '#cbd5e1',
                    border: 'none',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                >
                  <FaMapMarkerAlt /> Por Estado (UF)
                </button>
                <button
                  type='button'
                  onClick={() => setViewMode('distribuidor')}
                  style={{
                    background: viewMode === 'distribuidor' ? '#a6c10b' : 'transparent',
                    color: viewMode === 'distribuidor' ? '#091c3e' : '#cbd5e1',
                    border: 'none',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                >
                  <FaBuilding /> Por Distribuidor
                </button>
              </div>

              <SearchInputWrapper>
                <FaSearch />
                <input
                  type='text'
                  placeholder={
                    viewMode === 'uf'
                      ? 'Filtrar por UF ou Estado...'
                      : 'Filtrar por distribuidor, código, regional...'
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchInputWrapper>
            </div>

            <ExportButton
              onClick={() =>
                onExportExcel({
                  viewMode,
                  customDists: distsFiltrados,
                  customUfs: ufsFiltradas,
                  tabName: 'evolucao',
                })
              }
              title={
                viewMode === 'distribuidor'
                  ? 'Exportar dinâmica de positivação por distribuidor'
                  : 'Exportar dinâmica de positivação por estado (UF)'
              }
            >
              <FaFileExcel />
              Exportar Excel
            </ExportButton>
          </TableControls>

          <TableScroll>
            {viewMode === 'uf' ? (
              <StyledTable>
                <thead>
                  <tr>
                    <th>UF</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'right' }}>Cob. Anterior</th>
                    <th style={{ textAlign: 'right' }}>Cob. Atual</th>
                    <th style={{ textAlign: 'right', color: '#22c55e' }}>Ganhos (+)</th>
                    <th style={{ textAlign: 'right', color: '#ef4444' }}>Perdidos (-)</th>
                    <th style={{ textAlign: 'right' }}>Mantidos</th>
                    <th style={{ textAlign: 'right' }}>Saldo Líquido</th>
                    <th style={{ textAlign: 'right' }}>Variação %</th>
                    <th style={{ textAlign: 'right' }}>Taxa Churn %</th>
                  </tr>
                </thead>
                <tbody>
                  {ufsFiltradas.map((item: CoberturaPorUF) => {
                    const saldoPositivo = item.saldo_liquido >= 0
                    return (
                      <tr key={item.uf}>
                        <td style={{ fontWeight: 'bold', color: '#a6c10b' }}>{item.uf}</td>
                        <td>{item.estado}</td>
                        <td style={{ textAlign: 'right', color: '#94a3b8' }}>
                          {item.cobertura_anterior.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: isLight ? '#0f172a' : '#ffffff' }}>
                          {item.cobertura_atual.toLocaleString('pt-BR')}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: '#22c55e',
                          }}
                        >
                          +{item.ganhos.toLocaleString('pt-BR')}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: '#ef4444',
                          }}
                        >
                          -{item.perdidos.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right', color: '#94a3b8' }}>
                          {item.mantidos.toLocaleString('pt-BR')}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: saldoPositivo ? '#22c55e' : '#ef4444',
                          }}
                        >
                          {saldoPositivo ? '+' : ''}
                          {item.saldo_liquido.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <TrendBadge
                            $type={
                              item.taxa_evolucao_pct > 0
                                ? 'positive'
                                : item.taxa_evolucao_pct < 0
                                ? 'negative'
                                : 'neutral'
                            }
                          >
                            {item.taxa_evolucao_pct >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                            {item.taxa_evolucao_pct >= 0 ? '+' : ''}
                            {item.taxa_evolucao_pct.toFixed(2)}%
                          </TrendBadge>
                        </td>
                        <td style={{ textAlign: 'right', color: '#ef4444' }}>
                          {item.taxa_churn_pct.toFixed(2)}%
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </StyledTable>
            ) : (
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Pos</th>
                    <th>Cód.</th>
                    <th>Distribuidor</th>
                    <th>Regional</th>
                    <th>UF Dominante</th>
                    <th style={{ textAlign: 'right' }}>Cob. Anterior</th>
                    <th style={{ textAlign: 'right' }}>Cob. Atual</th>
                    <th style={{ textAlign: 'right', color: '#22c55e' }}>Ganhos (+)</th>
                    <th style={{ textAlign: 'right', color: '#ef4444' }}>Perdidos (-)</th>
                    <th style={{ textAlign: 'right' }}>Saldo Líquido</th>
                    <th style={{ textAlign: 'right' }}>Variação %</th>
                  </tr>
                </thead>
                <tbody>
                  {distsFiltrados.map((d: CoberturaPorDistribuidor) => {
                    const saldoPositivo = d.saldo_liquido >= 0
                    return (
                      <tr key={`${d.dist_id}-${d.posicao}`}>
                        <td>{d.posicao}</td>
                        <td style={{ color: '#94a3b8' }}>{d.cod_dist}</td>
                        <td style={{ fontWeight: '600' }}>{d.distribuidor}</td>
                        <td>{d.regional}</td>
                        <td>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span style={{ color: '#a6c10b', fontWeight: 'bold' }}>
                              {d.uf_principal || d.uf}
                            </span>
                            {d.ufs_invasao && d.ufs_invasao.length > 0 && (
                              <InvasionBadge
                                $isWarning={d.status_territorio === 'quebra'}
                                title={
                                  d.status_territorio === 'quebra'
                                    ? d.alerta_territorio
                                    : `Invasão em: ${d.ufs_invasao
                                        .map((u) => `${u.uf} (${u.cobertura})`)
                                        .join(', ')}`
                                }
                              >
                                {d.status_territorio === 'quebra' ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginRight: '3px' }}>
                                    <FaExclamationTriangle size={10} /> Quebra
                                  </span>
                                ) : (
                                  '+ '
                                )}
                                {d.ufs_invasao
                                  .map((u) => `${u.uf} (${u.cobertura})`)
                                  .join(', ')}
                              </InvasionBadge>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', color: '#94a3b8' }}>
                          {d.cobertura_anterior.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: isLight ? '#0f172a' : '#ffffff' }}>
                          {d.cobertura_atual.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#22c55e' }}>
                          +{d.ganhos.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#ef4444' }}>
                          -{d.perdidos.toLocaleString('pt-BR')}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: saldoPositivo ? '#22c55e' : '#ef4444',
                          }}
                        >
                          {saldoPositivo ? '+' : ''}
                          {d.saldo_liquido.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <TrendBadge
                            $type={
                              d.taxa_evolucao_pct > 0
                                ? 'positive'
                                : d.taxa_evolucao_pct < 0
                                ? 'negative'
                                : 'neutral'
                            }
                          >
                            {d.taxa_evolucao_pct >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                            {d.taxa_evolucao_pct >= 0 ? '+' : ''}
                            {d.taxa_evolucao_pct.toFixed(2)}%
                          </TrendBadge>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </StyledTable>
            )}
          </TableScroll>
        </TableWrapper>
      </div>
    </>
  )
}
